import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import InputBox from '../../components/InputBox';
import Button from '../../components/Button';
import Checkbox from '../../components/Checkbox';
import Modal from '../../components/Modal';
import GoogleSignIn from '../../components/GoogleSignIn';
import { useSettings } from '../../contexts/SettingsContext';
import { getAppSecretKey, setMetadata } from '../../utils/encryptedStorage';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { settings } = useSettings();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    rememberMe: false,
    role: 'HR',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const [successMessage, setSuccessMessage] = useState('');
  
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  // Reset password state
  const [resetToken, setResetToken] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetErrors, setResetErrors] = useState({});
    // Show reset password form if token param exists
    useEffect(() => {
      const token = searchParams.get('token');
      if (token) {
        setShowReset(true);
        setResetToken(token);
      } else {
        setShowReset(false);
        setResetToken('');
      }
    }, [searchParams]);
    // Reset password form validation
    const validateResetForm = () => {
      const newErrors = {};
      if (!resetPassword) newErrors.password = 'Password is required';
      if (!resetConfirmPassword) newErrors.confirmPassword = 'Confirm password is required';
      if (resetPassword && resetConfirmPassword && resetPassword !== resetConfirmPassword) newErrors.confirmPassword = 'Passwords do not match';
      setResetErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    // Handle reset password submit
    const handleResetSubmit = async (e) => {
      e.preventDefault();
      if (!validateResetForm()) return;
      setIsLoading(true);
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const SECRET_KEY = getAppSecretKey();
        if (!SECRET_KEY) {
          setResetErrors({ password: 'Missing REACT_APP_SECRETKEY in ux/.env (used for encrypting payload).' });
          setIsLoading(false);
          return;
        }
        const payload = { resetToken, password: resetPassword, confirm_password: resetConfirmPassword };
        const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();
        const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: encrypted }),
        });
        const data = await response.json();
        if (response.ok && (data.message?.toLowerCase().includes('success') || data.status === 'success')) {
          setModal({
            isOpen: true,
            type: 'success',
            title: 'Password Reset',
            message: 'Your password has been reset successfully. You can now log in.',
          });
          setShowReset(false);
          setResetPassword('');
          setResetConfirmPassword('');
          setResetErrors({});
          navigate('/auth/login');
        } else {
          setModal({
            isOpen: true,
            type: 'error',
            title: 'Reset Failed',
            message: data.error || 'Failed to reset password. Please try again.',
          });
        }
      } catch (error) {
        setModal({
          isOpen: true,
          type: 'error',
          title: 'Network Error',
          message: 'Could not connect to server. Please try again later.',
        });
      } finally {
        setIsLoading(false);
      }
    };
  
  // Modal state
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';

  // Check for activation or registration success from URL params
  useEffect(() => {
    if (searchParams.get('activated') === 'true') {
      setModal({
        isOpen: true,
        type: 'success',
        title: 'Account Activated',
        message: 'Your account has been activated successfully! You can now log in.',
      });
      setSuccessMessage('Your account has been activated successfully! You can now log in.');
      // Clear URL params to prevent modal from showing again on refresh
      window.history.replaceState({}, '', '/auth/login');
    } else if (searchParams.get('registered') === 'true') {
      setModal({
        isOpen: true,
        type: 'success',
        title: 'Registration Successful',
        message: 'Registration successful! Please check your email to activate your account.',
      });
      setSuccessMessage('Registration successful! Please check your email to activate your account.');
      // Clear URL params to prevent modal from showing again on refresh
      window.history.replaceState({}, '', '/auth/login');
    }
  }, [searchParams]);

  // Helper to close modal
  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }));
  };

  // Helper to show error modal (available for future use)
  // eslint-disable-next-line no-unused-vars
  const showErrorModal = (title, message) => {
    setModal({
      isOpen: true,
      type: 'error',
      title,
      message,
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const SECRET_KEY = getAppSecretKey();

      if (!SECRET_KEY) {
        setErrors({
          email: 'Missing REACT_APP_SECRETKEY in ux/.env (used for encrypting login payload).',
        });
        return;
      }

      // Encrypt full payload (same as eeezo_main style): CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString()
      const payload = {
        email: formData.email,
        password: formData.password,
        role: 'HR',
        recruiter: true,
        candidate: false,
      };
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();

      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: encrypted,
        }),
      });

      const data = await response.json();

      // Backend success shape: { message: 'Success', data: <encrypted-string>, token?: <jwt> }
      if (response.ok && (data.message === 'Success' || data.status === 'success')) {
        let userData = null;
        if (data.data) {
          try {
            if (typeof data.data === 'string') {
              // Decrypt encrypted data from backend
              const bytes = CryptoJS.AES.decrypt(data.data, SECRET_KEY);
              const decrypted = bytes.toString(CryptoJS.enc.Utf8);
              userData = JSON.parse(decrypted);
            } else {
              // Fallback: if backend ever sends plain object
              userData = data.data;
            }
          } catch (err) {
            console.error('Failed to decrypt login response data:', err);
            setErrors({
              email: 'Failed to decrypt server response. Please contact support.',
            });
            return;
          }
        }

        // Store session details in localStorage as 'metadata' (as per requirements)
        // The metadata variable should contain the user details directly (not wrapped in a 'user' property)
        // Fields: uid, email, role_id, phone_no, name, designation, profile, cid, created_at, 
        //         created_by, status, notification, candidate_id, refresh_token, passcode, 
        //         subscription_id, fitment_level, company_name, plan_id
        const metadata = {
          uid: userData?.uid || userData?.id || null,
          email: userData?.email || '',
          role_id: userData?.role_id || 1,
          phone_no: userData?.phone_no || '',
          name: userData?.name || '',
          designation: userData?.designation || null,
          profile: userData?.profile || null,
          cid: userData?.cid || null,
          created_at: userData?.created_at || null,
          created_by: userData?.created_by || null,
          status: userData?.status ?? true,
          notification: userData?.notification || 'false',
          candidate_id: userData?.candidate_id || null,
          refresh_token: userData?.refresh_token || null,
          passcode: userData?.passcode || null,
          subscription_id: userData?.subscription_id || null,
          fitment_level: userData?.fitment_level || 'RELAXED',
          company_name: userData?.company_name || null,
          plan_id: userData?.plan_id || null,
          token: data.token || null,
        };
        // Store encrypted metadata
        setMetadata(metadata);
        
        // Also store token separately for easy access
        if (data.token) {
          localStorage.setItem('auth_token', data.token);
        }

        // Keep backward compatibility - also store user separately
        if (userData) {
          localStorage.setItem('user', JSON.stringify(userData));
        }

        // Check if user has company (cid). If not, redirect to company creation page
        // New users without a company need to create one first
        if (!metadata.cid) {
          navigate('/auth/workspace-setup');
        } else {
          navigate('/dashboard');
        }
      } else {
        setErrors({
          email: data.error || 'Invalid credentials. Please check your email and password.',
        });
      }
    } catch (error) {
      console.error('Login failed:', error);
      setErrors({ 
        email: 'Connection error. Please make sure the backend server is running.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail || !/\S+@\S+\.\S+/.test(forgotEmail)) {
      setErrors({ resetEmail: 'Please enter a valid email address' });
      return;
    }
    setIsLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const SECRET_KEY = process.env.REACT_APP_SECRETKEY;
      if (!SECRET_KEY) {
        setErrors({ resetEmail: 'Missing REACT_APP_SECRETKEY in ux/.env (used for encrypting payload).' });
        setIsLoading(false);
        return;
      }
      // Only send email and candidate flag (if job seeker)
      const payload = {
        email: forgotEmail,
        candidate: false,
      };
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: encrypted }),
      });
      const data = await response.json();
      if (response.ok && (data.message?.toLowerCase().includes('sent') || data.status === 'success')) {
        setModal({
          isOpen: true,
          type: 'success',
          title: 'Reset Link Sent',
          message: 'If this email is registered, a password reset link has been sent. Please check your inbox.',
        });
        setForgotEmail('');
        setErrors({});
      } else {
        setModal({
          isOpen: true,
          type: 'error',
          title: 'Reset Failed',
          message: data.error || 'Failed to send reset link. Please try again.',
        });
      }
    } catch (error) {
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Network Error',
        message: 'Could not connect to server. Please try again later.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-gray-50 dark:bg-gray-900 flex items-stretch transition-[background-color] duration-250 ease-out">
      {/* Left Brand Panel (desktop) - Auth.svg Image */}
      <div className="hidden lg:flex lg:w-[45%] transition-colors duration-250">
        {/* Outer padding wrapper */}
        <div className="w-full h-full py-4 pl-4 pr-2">
          {/* Rounded image container - full size */}
          <div className="relative w-full h-full rounded-3xl overflow-hidden">
            <img
              src="/assets/auth/Auth.svg"
              alt="Welcome illustration"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Right Content - Login Form - ~55% width */}
<div className="flex-1 flex flex-col lg:w-[55%] min-w-0 transition-colors duration-250 h-full overflow-y-auto">
  {/* Login Content - center aligned with larger scale */}
  <main
    className="flex-1 flex items-center justify-center"
    style={{
      paddingLeft: 'clamp(8px, 4vw, 64px)',
      paddingRight: 'clamp(8px, 4vw, 64px)',
      paddingTop: 'clamp(8px, 3vw, 32px)',
      paddingBottom: 'clamp(8px, 3vw, 32px)'
    }}
  >
    <div className="w-full max-w-2xl">
      <div>
        {/* Success Message Banner */}
        {successMessage && (
          <div className="mb-6 p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 transition-colors duration-250">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-green-700 dark:text-green-300">{successMessage}</p>
            </div>
          </div>
        )}

        {/* Heading (only show if not resetting password) */}
        {!showReset && (
          <div className="mb-6 mt-1">
            <h1 className="text-5xl font-medium tracking-normal transition-colors duration-250 text-gray-700 dark:text-gray-200">
              {showForgot ? 'Reset Password' : 'Welcome !'}
            </h1>
            <p className="mt-1.5 text-base font-normal transition-colors duration-250 text-gray-500 dark:text-gray-400">
              {showForgot 
                ? "Enter your email to receive a password reset link" 
                : "Let's get your hiring started."}
            </p>
          </div>
        )}

        {showReset ? (
          <>
            {/* Reset Password Header */}
            <div className="mb-4 mt-1">
              <h1 className="text-5xl font-medium tracking-normal transition-colors duration-250 text-gray-700 dark:text-gray-200">
                Reset Password
              </h1>
              <p className="mt-1.5 text-base font-normal transition-colors duration-250 text-gray-500 dark:text-gray-400">
                Enter your new password below to reset your account.
              </p>
            </div>
            {/* Reset Password Form (inline) */}
            <form onSubmit={handleResetSubmit} className="space-y-4">
              <InputBox
                label="New Password"
                type="password"
                name="resetPassword"
                placeholder="Enter new password"
                value={resetPassword}
                onChange={e => setResetPassword(e.target.value)}
                error={resetErrors.password}
                required
              />
              <InputBox
                label="Confirm Password"
                type="password"
                name="resetConfirmPassword"
                placeholder="Re-enter new password"
                value={resetConfirmPassword}
                onChange={e => setResetConfirmPassword(e.target.value)}
                error={resetErrors.confirmPassword}
                required
              />
              <div className="pt-1">
                <Button
                  type="submit"
                  size="lg"
                  loading={isLoading}
                  disabled={isLoading || !resetPassword || !resetConfirmPassword}
                  fullWidth
                >
                  Reset Password
                </Button>
              </div>
            </form>
            {/* Back to Login */}
            <div className="mt-4">
              <button
                type="button"
                onClick={() => {
                  setShowReset(false);
                  setResetPassword('');
                  setResetConfirmPassword('');
                  setResetErrors({});
                  navigate('/auth/login');
                }}
                className="flex items-center gap-2 text-sm font-normal transition-colors duration-250 hover:underline text-gray-500 dark:text-gray-400"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Login
              </button>
            </div>
          </>
        ) : !showForgot ? (
          <>
            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-3">
              <InputBox
                label="Name"
                type="text"
                name="name"
                placeholder="Enter your name"
                value={formData.name}
                onChange={handleChange}
                autoComplete="name"
              />

              <InputBox
                label="Email ID"
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
              />

              <div>
                <div className="flex items-baseline justify-between mb-2">
                  <label className="block text-sm font-normal transition-colors duration-250 text-slate-600 dark:text-slate-400">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgot(true)}
                    className="text-sm font-normal transition-colors duration-250 hover:underline text-gray-500 dark:text-gray-400"
                  >
                    Forgot Password?
                  </button>
                </div>
                <InputBox
                  type="password"
                  name="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  error={errors.password}
                  required
                  autoComplete="current-password"
                />

                {/* Remember Me */}
                <div className="flex items-center mt-3">
                  <Checkbox
                    label="Remember Me"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-1">
                <Button
                  type="submit"
                  size="lg"
                  loading={isLoading}
                  disabled={!formData.email || !formData.password}
                  fullWidth
                >
                  Log In
                </Button>
              </div>
            </form>

            {/* Error Message Banner */}
            {errors.email && (
              <div className="mt-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 transition-colors duration-250">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-red-700 dark:text-red-300">{errors.email}</p>
                </div>
              </div>
            )}

            {/* OR Divider */}
            <div className="mt-3.5 flex items-center gap-4">
              <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
              <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">Or</span>
              <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
            </div>

            {/* Google Sign In */}
            <div className="mt-3">
              <GoogleSignIn
                onError={(message) => setErrors({ email: message })}
                onSuccess={(message) => setSuccessMessage(message)}
                setIsLoading={setIsLoading}
                candidate={false}
              />
            </div>

            {/* Sign Up link */}
            <p className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400 transition-colors duration-250">
              Don&apos;t have an account?{' '}
              <Link
                to="/auth/signup"
                className="font-medium hover:underline"
                style={{ color: primaryColor }}
              >
                Sign Up
              </Link>
            </p>
          </>
        ) : (
          <>
            {/* Forgot Password Form */}
            <form onSubmit={handleForgotSubmit} className="space-y-4">
              <InputBox
                label="Email ID"
                type="email"
                name="resetEmail"
                placeholder="Enter your email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                autoComplete="email"
                error={errors.resetEmail}
              />

              {/* Submit Button */}
              <div className="pt-1">
                <Button
                  type="submit"
                  size="lg"
                  loading={isLoading}
                  disabled={!forgotEmail}
                  fullWidth
                >
                  Send Reset Link
                </Button>
              </div>
            </form>

            {/* Back to Login */}
            <div className="mt-4">
              <button
                type="button"
                onClick={() => {
                  setShowForgot(false);
                  setForgotEmail('');
                  setErrors({});
                }}
                className="flex items-center gap-2 text-sm font-normal transition-colors duration-250 hover:underline text-gray-500 dark:text-gray-400"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  </main>
</div>

      {/* Modal for success/error messages */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        confirmText="Got it"
      />
    </div>
  );
};

export default LoginPage;
